import { HiddenAiTranslationHint, KiTranslatedBadge } from '../../components/atoms.js';
import type { CatalogColumn } from '../../components/catalog-table.js';
import type { Step } from '../../lib/catalog-api.js';

// #81b: Drei Sprachmodi fuer die Schritt-Tabelle — sieben statt zehn Spalten in DE/EN, alle
// zehn in 'both'. Vorbelegung + Persistenz in der URL uebernimmt der Aufrufer (step-table.tsx).
export type StepLanguageMode = 'de' | 'en' | 'both';

// #28: Die Spalten leben getrennt von der Komponente, damit der Durchstich-Guard sie gegen
// StepCreateSchema stellen kann, ohne die Tabelle zu rendern (Spec Kapitel 2.2).
export type StepDraft = {
  description_de: string;
  description_en: string;
  tcode: string | null;
  expected_output_de: string | null;
  expected_output_en: string | null;
  // #66: Schritt-Input ("Input Data") — symmetrisch zu expected_output_*.
  input_de: string | null;
  input_en: string | null;
  notes: string | null;
};

export const emptyStepDraft: StepDraft = {
  description_de: '',
  description_en: '',
  tcode: null,
  expected_output_de: null,
  expected_output_en: null,
  input_de: null,
  input_en: null,
  notes: null,
};

export function toStepDraft(s: Step): StepDraft {
  return {
    description_de: s.description_de,
    description_en: s.description_en,
    tcode: s.tcode,
    expected_output_de: s.expected_output_de,
    expected_output_en: s.expected_output_en,
    input_de: s.input_de,
    input_en: s.input_en,
    notes: s.notes,
  };
}

export function stepColumns(
  t: (k: string, o?: Record<string, unknown>) => string,
  mode: StepLanguageMode,
): CatalogColumn<Step, StepDraft>[] {
  const showDe = mode !== 'en';
  const showEn = mode !== 'de';

  // C1-Fix (Review-Finding): description_de/description_en sind in StepCreateSchema die einzigen
  // beiden Pflichtfelder (description_de ueber `.min(1)`, description_en ist ihr untrennbares
  // Gegenstueck) — sie duerfen NICHT vom Sprachmodus ausgeblendet werden. Vorher blendete
  // `showDe`/`showEn` sie wie jede andere Sprachspalte aus: ein EN-Nutzer (fuer den 'en' der
  // DEFAULT-Modus ist, siehe step-table.tsx) sah beim Anlegen gar kein Feld fuer description_de,
  // der Server lehnte trotzdem mit 400 ab — Sackgasse ohne Ausweg. Gefiltert bleiben NUR die
  // optionalen Sprachfelder input_*/expected_output_* — die reduzieren wie vorgesehen von zehn auf
  // sieben Spalten in einem Ein-Sprach-Modus, nur eben eine Spalte mehr als zuvor (description_de/
  // description_en zaehlen jetzt IMMER mit).
  const showOptionalDe = showDe;
  const showOptionalEn = showEn;

  // #81b: Blendet der Modus eine Sprache aus, darf ein `*_ai`-Abzeichen dieser Sprache nicht
  // spurlos verschwinden — sonst versteckt der Umschalter genau die Warnung, fuer die #74 gebaut
  // wurde. Der Hinweis haengt deshalb an der IMMER sichtbaren #-Spalte statt an einem Feld, das
  // fachlich gar nicht die AI-Quelle ist. description_*_ai zaehlt hier nicht mehr mit: description_de/
  // description_en sind seit dem C1-Fix ohnehin in jedem Modus sichtbar, ihr Badge kann also nie
  // "versteckt" sein.
  const hiddenAiLanguage = (r: Step): 'de' | 'en' | null => {
    if (mode === 'de' && (r.input_en_ai || r.expected_output_en_ai)) {
      return 'en';
    }
    if (mode === 'en' && (r.input_de_ai || r.expected_output_de_ai)) {
      return 'de';
    }
    return null;
  };

  const columns: CatalogColumn<Step, StepDraft>[] = [
    {
      // Fest: der Server vergibt step_no aus der Reihenfolge. Im Bearbeiten-Zustand Text.
      key: 'step_no',
      header: t('catalog.fields.stepNo'),
      width: '4rem',
      render: (r) => {
        const hidden = hiddenAiLanguage(r);
        return (
          <span className="flex items-center gap-1">
            <span>{r.step_no}</span>
            {hidden && <HiddenAiTranslationHint language={hidden} />}
          </span>
        );
      },
    },
  ];

  // C1-Fix: description_de/description_en sind Pflichtfelder (siehe Kommentar oben) und stehen
  // deshalb ausserhalb der showDe/showEn-Filterung — sonst waeren sie in einem Ein-Sprach-Modus
  // fuer diese Sprache unerreichbar.
  columns.push({
    key: 'description_de',
    header: `${t('catalog.fields.description')} (DE)`,
    // Task 10 (#63/#64): KI-Übersetzt-Badge, solange description_de_ai steht (EN-Quell-Import).
    // Spiegelbild von description_en unten.
    render: (r) => (
      <span className="flex items-center gap-1.5">
        <span>{r.description_de}</span>
        {r.description_de_ai && <KiTranslatedBadge />}
      </span>
    ),
    editor: {
      kind: 'textarea',
      get: (d) => d.description_de,
      set: (d, v) => ({ ...d, description_de: v }),
    },
  });

  columns.push({
    key: 'description_en',
    header: `${t('catalog.fields.description')} (EN)`,
    render: (r) => (
      <span className="flex items-center gap-1.5">
        <span>{r.description_en || '—'}</span>
        {r.description_en_ai && <KiTranslatedBadge />}
      </span>
    ),
    editor: {
      kind: 'textarea',
      get: (d) => d.description_en,
      set: (d, v) => ({ ...d, description_en: v }),
    },
  });

  if (showOptionalDe) {
    columns.push({
      // #66: Schritt-Input ("Input Data") — dritter Schritt-Text neben description_* und
      // expected_output_*, gleiches DE/EN + KI-Abzeichen-Muster (Spiegelbild description_de).
      key: 'input_de',
      header: `${t('catalog.fields.input')} (DE)`,
      render: (r) => (
        <span className="flex items-center gap-1.5">
          <span>{r.input_de ?? '—'}</span>
          {r.input_de_ai && <KiTranslatedBadge />}
        </span>
      ),
      editor: {
        kind: 'textarea',
        get: (d) => d.input_de ?? '',
        set: (d, v) => ({ ...d, input_de: v || null }),
      },
    });
  }

  if (showOptionalEn) {
    columns.push({
      key: 'input_en',
      header: `${t('catalog.fields.input')} (EN)`,
      // Spiegelbild input_de oben.
      render: (r) => (
        <span className="flex items-center gap-1.5">
          <span>{r.input_en || '—'}</span>
          {r.input_en_ai && <KiTranslatedBadge />}
        </span>
      ),
      editor: {
        kind: 'textarea',
        get: (d) => d.input_en ?? '',
        set: (d, v) => ({ ...d, input_en: v || null }),
      },
    });
  }

  // Sprachneutral — steht in jedem Modus, wie step_no und notes unten.
  columns.push({
    key: 'tcode',
    header: t('catalog.fields.tcode'),
    width: '8rem',
    render: (r) => r.tcode ?? '—',
    editor: {
      kind: 'text',
      get: (d) => d.tcode ?? '',
      set: (d, v) => ({ ...d, tcode: v || null }),
    },
  });

  if (showOptionalDe) {
    columns.push({
      key: 'expected_output_de',
      header: `${t('catalog.fields.expectedOutput')} (DE)`,
      // Task 10 (#63/#64): KI-Übersetzt-Badge, solange expected_output_de_ai steht (EN-Quell-
      // Import). Spiegelbild von expected_output_en unten.
      render: (r) => (
        <span className="flex items-center gap-1.5">
          <span>{r.expected_output_de ?? '—'}</span>
          {r.expected_output_de_ai && <KiTranslatedBadge />}
        </span>
      ),
      editor: {
        kind: 'textarea',
        get: (d) => d.expected_output_de ?? '',
        set: (d, v) => ({ ...d, expected_output_de: v || null }),
      },
    });
  }

  if (showOptionalEn) {
    columns.push({
      // #28: existierte in Schema und API, hatte aber nie eine Spalte — sechster Fall der
      // Klasse „Datenmodell OK - API OK - Bedienelement fehlt".
      key: 'expected_output_en',
      header: `${t('catalog.fields.expectedOutput')} (EN)`,
      render: (r) => (
        <span className="flex items-center gap-1.5">
          <span>{r.expected_output_en || '—'}</span>
          {r.expected_output_en_ai && <KiTranslatedBadge />}
        </span>
      ),
      editor: {
        kind: 'textarea',
        get: (d) => d.expected_output_en ?? '',
        set: (d, v) => ({ ...d, expected_output_en: v || null }),
      },
    });
  }

  columns.push({
    key: 'notes',
    header: t('catalog.fields.notes'),
    render: (r) => r.notes ?? '—',
    editor: {
      kind: 'textarea',
      get: (d) => d.notes ?? '',
      set: (d, v) => ({ ...d, notes: v || null }),
    },
  });

  return columns;
}
